import {createHttpClient} from '@/services/HttpService';
import type {Filter, FilterGroup, FilterItem} from '@/lib/filters/types';

const client = createHttpClient('CLOUD_RUN');

export interface SlimMarker {
  id: string;
  name: string;
  location: {latitude: number; longitude: number};
  editedOn: unknown;
  projectStatus?: string;
  displayFields?: {label: string; value: unknown}[];
  comps?: {id: string; name: string; type: string}[];
  source?: 'salesforce' | 'entrata';
  linkedDocuments?: {collection: string; id: string}[];
  generatedByOm?: boolean;
}

export interface MarkersResponse {
  markers: SlimMarker[];
  /** ISO server time this response describes. */
  asOf: string;
  /** 'delta' when a valid `since` was sent; 'full' (a complete restatement) when it was omitted. */
  mode: 'full' | 'delta';
  /** Ids of markers deleted since `since`. Delta mode only. */
  deletedIds?: string[];
}

/** Same freshness envelope as MarkersResponse; the items are Light OM Items, not markers. */
export interface LightItemsResponse {
  markers: Array<Record<string, unknown> & {id: string}>;
  asOf: string;
  mode: 'full' | 'delta';
  deletedIds?: string[];
}

export type LightCollection = 'omBuildings' | 'omPortfolios';

export interface CollectionFilterRequest {
  collection: string;
  filter: FilterGroup;
  compViewMode?: string;
}

export interface CollectionFilterResponse {
  ids: string[] | null;
}

/** A doc version the caller just wrote; the backend waits for its cache to hold it before filtering. */
export interface AwaitDoc {
  /** Public (filter) collection name */
  collection: string;
  id: string;
  /** The editedOn.timestamp the caller wrote, in epoch millis */
  editedAtMillis: number;
}

/** Batch entries answer with ids on success, or an error when that collection is unavailable. */
export interface BatchEntryResponse {
  ids?: string[] | null;
  error?: string;
}

export interface FilterCountsResponse {
  total: number;
  counts: Record<string, Record<string, number>>;
}

/**
 * Service for communicating with the filterMarkers Cloud Run backend.
 *
 * Handles:
 * - Fetching pre-slimmed marker data for a collection
 * - Filtering a collection by a single filter group
 * - Batch-filtering multiple collections at once
 * - Fetching server-side filter option counts
 *
 * Auth is managed internally by HttpClient (Firebase Bearer token).
 */
const FilterService = {
  /**
   * Fetches slim marker data for a collection from the backend.
   * The returned documents contain only the fields needed for marker rendering.
   * Pass a previously returned `asOf` as `since` to request a delta; omit it to request a full
   * response. Callers must branch on `mode`.
   */
  async fetchSlimMarkers(collection: string, since?: string): Promise<MarkersResponse> {
    const body = since ? {collection, since} : {collection};
    return client.post<MarkersResponse>('filtermarkers', body, {path: 'markers'});
  },

  /** OM review items as the shelf's fields only; the full document is fetched on open. */
  async fetchLightItems(collection: LightCollection): Promise<LightItemsResponse> {
    return client.post<LightItemsResponse>('filtermarkers', {collection}, {path: 'light-items'});
  },

  /**
   * Filters a collection by a single filter group and returns the matching document IDs.
   * Returns null if the filter group has no active filters (all items pass).
   */
  async filterCollection(collection: string, filter: FilterGroup, compViewMode?: string): Promise<string[] | null> {
    const {ids} = await client.post<CollectionFilterResponse>('filtermarkers', {collection, filter, compViewMode}, {path: 'filter'});
    return ids;
  },

  /**
   * Filters multiple collections in a single request.
   * Responses are returned in the same order as the requests.
   */
  async filterBatch(requests: CollectionFilterRequest[], awaitDocs?: AwaitDoc[]): Promise<BatchEntryResponse[]> {
    return client.post<BatchEntryResponse[]>('filtermarkers', {requests, awaitDocs}, {path: 'batch'});
  },

  /**
   * Fetches server-side filter option counts for a collection given the currently active filters.
   * Returns a nested map: filterLabel → optionValue → count.
   */
  async getFilterCounts(
    collection: string,
    filters: Filter[],
    polygonFilterPath?: Array<{lat: number; lng: number}> | null,
  ): Promise<FilterCountsResponse> {
    return client.post<FilterCountsResponse>('filtermarkers', {collection, filters, polygonFilterPath}, {path: 'counts'});
  },

  /**
   * Fetches the distinct selectable options for an autocomplete filter, enumerated
   * server-side from full documents (slim markers lack the source fields).
   */
  async getFilterOptions(
    collection: string,
    descriptor: {key?: string; itemTitle?: string; itemId?: string},
  ): Promise<FilterItem[]> {
    const {items} = await client.post<{items: FilterItem[]}>(
      'filtermarkers', {collection, ...descriptor}, {path: 'autocomplete-options'});
    return items;
  },
};

export {FilterService};
